import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CurrencyDollarIcon, ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useLocation } from '../../contexts/LocationContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';

interface GoatIncomeLog {
  id: string;
  location_id: string;
  shed_id?: string;
  sale_date: string;
  amount_earned: number;
  quantity_sold_kg?: number;
  goat_count?: number;
  goat_ids?: string[];
  avg_weight_kg?: number;
  buyer_name?: string;
  buyer_contact?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface GoatMaster {
  id: string;
  tag_no: string;
  category: string;
  breed?: string;
  sale_amount?: number;
}

const GoatSaleRegister: React.FC = () => {
  const { businessType } = useBusinessTypeContext();
  const { selectedLocation } = useLocation();
  const [logs, setLogs] = useState<GoatIncomeLog[]>([]);
  const [goatsMap, setGoatsMap] = useState<Record<string, GoatMaster>>({});
  const [sheds, setSheds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [selectedShed, setSelectedShed] = useState('');
  const [showOnlyGoatSales, setShowOnlyGoatSales] = useState(false);

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadSheds();
    }
  }, [selectedLocation?.id, businessType]);

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadLogs();
    }
  }, [selectedLocation?.id, businessType, startDate, endDate, selectedShed]);

  const loadSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err: any) {
      console.error('Failed to load sheds:', err);
    }
  };

  const loadGoats = async (goatIds: string[]) => {
    if (goatIds.length === 0) return;
    
    try {
      const goats: GoatMaster[] = [];
      for (const goatId of goatIds) {
        try {
          const response = await apiClient.get(`/goat/master/${goatId}`);
          goats.push(response.data);
        } catch (err) {
          console.error(`Failed to load goat ${goatId}:`, err);
        }
      }
      
      const map: Record<string, GoatMaster> = {};
      goats.forEach(goat => {
        map[goat.id] = goat;
      });
      setGoatsMap(prev => ({ ...prev, ...map }));
    } catch (err: any) {
      console.error('Failed to load goats:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ location_id: selectedLocation?.id || '' });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedShed) params.append('shed_id', selectedShed);
      
      const response = await apiClient.get(`/goat/income-logs?${params.toString()}`);
      const logsData = response.data || [];
      
      // Filter to show only goat sales if filter is enabled
      let filteredLogs = logsData;
      if (showOnlyGoatSales) {
        filteredLogs = logsData.filter((log: GoatIncomeLog) => 
          log.goat_ids && log.goat_ids.length > 0
        );
      }
      
      setLogs(filteredLogs);
      
      // Load goat details for all logs with goat_ids
      const allGoatIds: string[] = [];
      filteredLogs.forEach((log: GoatIncomeLog) => {
        if (log.goat_ids && log.goat_ids.length > 0) {
          allGoatIds.push(...log.goat_ids);
        }
      });
      
      // Remove duplicates
      const uniqueGoatIds = Array.from(new Set(allGoatIds));
      if (uniqueGoatIds.length > 0) {
        await loadGoats(uniqueGoatIds);
      }
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load sale records'));
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setSelectedShed('');
    setShowOnlyGoatSales(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTotalAmount = () => {
    return logs.reduce((sum, log) => sum + log.amount_earned, 0).toFixed(2);
  };

  const getTotalGoatSales = () => {
    return logs.filter(log => log.goat_ids && log.goat_ids.length > 0).length;
  };

  const getTotalGoatsSold = () => {
    return logs.reduce((sum, log) => {
      if (log.goat_ids && log.goat_ids.length > 0) {
        return sum + log.goat_ids.length;
      }
      return sum;
    }, 0);
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  const filteredLogs = showOnlyGoatSales 
    ? logs.filter(log => log.goat_ids && log.goat_ids.length > 0)
    : logs;

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💰 Sale Register</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/data-entry/income"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Record Sale</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="form-label">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Shed</label>
            <select
              value={selectedShed}
              onChange={(e) => setSelectedShed(e.target.value)}
              className="form-input"
            >
              <option value="">All Sheds</option>
              {sheds.map((shed) => (
                <option key={shed.id} value={shed.id}>
                  {shed.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Filter</label>
            <select
              value={showOnlyGoatSales ? 'goat_sales' : 'all'}
              onChange={(e) => setShowOnlyGoatSales(e.target.value === 'goat_sales')}
              className="form-input"
            >
              <option value="all">All Sales</option>
              <option value="goat_sales">Goat Sales Only</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Reset Filters
          </button>
          <button
            onClick={loadLogs}
            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {filteredLogs.length > 0 && (
        <div className="card p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-xs text-green-600 font-medium">Total Income</div>
              <div className="text-lg font-bold text-green-700">₹{getTotalAmount()}</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-xs text-blue-600 font-medium">Total Sales</div>
              <div className="text-lg font-bold text-blue-700">{filteredLogs.length}</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-xs text-purple-600 font-medium">Goat Sales</div>
              <div className="text-lg font-bold text-purple-700">{getTotalGoatSales()}</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-xs text-orange-600 font-medium">Goats Sold</div>
              <div className="text-lg font-bold text-orange-700">{getTotalGoatsSold()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Sale Records List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Sale Records ({filteredLogs.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : filteredLogs.length > 0 ? (
          <div className="space-y-4">
            {filteredLogs.map((log) => {
              const isGoatSale = log.goat_ids && log.goat_ids.length > 0;
              const shedName = log.shed_id ? (sheds.find(s => s.id === log.shed_id)?.name || 'Unknown Shed') : 'N/A';
              
              return (
                <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg font-bold text-green-600">
                          ₹{log.amount_earned.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        {isGoatSale && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                            Goat Sale
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center space-x-4">
                          <span className="font-medium">{formatDate(log.sale_date)}</span>
                          {shedName !== 'N/A' && <span className="text-gray-500">• {shedName}</span>}
                        </div>
                        {log.quantity_sold_kg && (
                          <div>Weight: {log.quantity_sold_kg} kg</div>
                        )}
                        {log.goat_count && (
                          <div>Goats: {log.goat_count}</div>
                        )}
                        {log.avg_weight_kg && (
                          <div>Avg Weight: {log.avg_weight_kg} kg/goat</div>
                        )}
                        {log.buyer_name && (
                          <div className="font-medium">Buyer: {log.buyer_name}</div>
                        )}
                        {log.buyer_contact && (
                          <div className="text-xs text-gray-500">Contact: {log.buyer_contact}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Show goat details if this is a goat sale */}
                  {isGoatSale && log.goat_ids && log.goat_ids.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-2">Goats Sold:</div>
                      <div className="flex flex-wrap gap-2">
                        {log.goat_ids.map((goatId) => {
                          const goat = goatsMap[goatId];
                          if (!goat) {
                            return (
                              <span key={goatId} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                Loading...
                              </span>
                            );
                          }
                          const categoryLabel = goat.category === 'doe' ? 'Doe' : goat.category === 'buck' ? 'Buck' : 'Kid';
                          const saleAmount = goat.sale_amount ? `₹${goat.sale_amount.toFixed(2)}` : 'N/A';
                          return (
                            <span 
                              key={goatId} 
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-medium"
                              title={`${goat.tag_no} - ${categoryLabel}${goat.breed ? ` (${goat.breed})` : ''} - Sale: ${saleAmount}`}
                            >
                              {goat.tag_no} ({categoryLabel}) - {saleAmount}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {log.remarks && (
                    <div className="mt-2 text-xs text-gray-500 italic">
                      Note: {log.remarks}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <CurrencyDollarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No sale records found</p>
            <Link
              to="/goat/data-entry/income"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Record First Sale
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoatSaleRegister;
